import {
  LayoutDashboard,
  Trophy,
  Images,
  Award,
  HeartHandshake,
  Bell,
  User,
  Home,
  Shield,
  Activity,
  Medal,
  Users,
  ScrollText,
  Palette,
  Megaphone,
  MessageCircle,
  Dumbbell,
  HeartPulse,
  Sparkles,
  Building2,
  Compass,
  KeyRound,
  Newspaper,
  MessageSquareHeart,
  ArrowLeft,
  BarChart3,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  requiresAuth: boolean;
  adminOnly?: boolean;
  workspaceAccessOnly?: boolean;
  workspaceManagerOnly?: boolean;
  /** Match only on an exact pathname rather than a prefix. */
  exact?: boolean;
  /** Resolved at render time against the user's active challenge slug. */
  challengeScoped?: boolean;
  /**
   * Where a `challengeScoped` item points when the user has no challenge to
   * scope it to. Without this the item is hidden; with it the item stays
   * visible and leads somewhere that can stand on its own.
   */
  unscopedHref?: string;
  /**
   * Hidden until the user has at least one registration. Use for pages that can
   * only ever show an empty state without a challenge.
   */
  requiresRegistration?: boolean;
  /** Shows the unread notification count. */
  badge?: "notifications" | "accessRequests";
  promotionalBadge?: "New";
  /** Additional routes that should keep this navigation item active. */
  activePrefixes?: string[];
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
  /** Sections marked collapsible can be expanded/collapsed independently. */
  collapsible?: boolean;
  /** Initial expanded state for collapsible sections. */
  defaultOpen?: boolean;
}

/**
 * The sidebar is grouped into sections. `challengeScoped` hrefs contain a
 * `:slug` placeholder that the sidebar swaps for the user's active challenge;
 * when the user has no registration those items are hidden.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "main",
    label: "Overview",
    collapsible: true,
    defaultOpen: true,
    items: [
      { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard, requiresAuth: true },
      {
        href: "/challenges",
        label: "Challenges",
        icon: Compass,
        requiresAuth: true,
        exact: true,
      },
      {
        href: "/challenges/:slug/tracker",
        label: "Steps Tracker",
        icon: Activity,
        requiresAuth: true,
        challengeScoped: true,
      },
      {
        href: "/challenges/:slug/leaderboard",
        label: "Leaderboard",
        icon: Medal,
        requiresAuth: true,
        challengeScoped: true,
        unscopedHref: "/leaderboard",
        activePrefixes: ["/leaderboard"],
      },
      {
        href: "/challenges/:slug/teams",
        label: "Teams",
        icon: Users,
        requiresAuth: true,
        challengeScoped: true,
      },
    ],
  },
  {
    id: "community",
    label: "Community",
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        href: "/community",
        label: "Community",
        icon: Newspaper,
        requiresAuth: true,
        promotionalBadge: "New",
      },
      { href: "/badges", label: "Badges", icon: Award, requiresAuth: true, requiresRegistration: true },
      { href: "/exercises", label: "Exercises", icon: Dumbbell, requiresAuth: true },
      { href: "/memories", label: "Memories", icon: Images, requiresAuth: true },
      { href: "/kudos", label: "Kudos", icon: HeartHandshake, requiresAuth: true, requiresRegistration: true },
    ],
  },
  {
    id: "account",
    label: "Account",
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        href: "/notifications",
        label: "Notifications",
        icon: Bell,
        requiresAuth: true,
        badge: "notifications",
      },
      { href: "/integrations", label: "Integrations", icon: HeartPulse, requiresAuth: true },
      { href: "/profile", label: "Profile", icon: User, requiresAuth: true },
      {
        href: "/feedback",
        label: "Ideas & feedback",
        icon: MessageSquareHeart,
        requiresAuth: true,
        promotionalBadge: "New",
      },
    ],
  },
  {
    id: "admin",
    label: "Manage",
    items: [
      {
        href: "/admin",
        label: "Admin",
        icon: Shield,
        requiresAuth: true,
        // Shown to workspace managers as well as portal administrators, because
        // `hasWorkspaceAccess` is already true for administrators. The admin area itself
        // filters its own rows, so each role only ever sees what it can use.
        workspaceAccessOnly: true,
        exact: true,
      },
    ],
  },
];

/**
 * The management area's own navigation, shown in place of the participant sections while the
 * user is inside it.
 *
 * Administration had grown to eleven rows sitting underneath fourteen participant ones in a
 * single scrolling rail, split across two groups whose boundary did not track anything a reader
 * could infer: access requests sat under "Administration" while portal users sat under
 * "Platform". Separating the two areas keeps each list short enough to scan, and grouping by the
 * thing being administered gives the boundary a rule.
 *
 * Rows carry the same role flags as the participant nav, so a workspace manager entering the
 * area sees only the campaign and workspace rows.
 */
export const ADMIN_NAV_SECTIONS: NavSection[] = [
  {
    id: "admin-exit",
    label: "",
    items: [
      {
        href: "/dashboard",
        label: "Back to portal",
        icon: ArrowLeft,
        requiresAuth: true,
      },
      { href: "/admin", label: "Overview", icon: Shield, requiresAuth: true, exact: true },
    ],
  },
  {
    id: "admin-people",
    label: "People",
    items: [
      { href: "/admin/users", label: "Portal users", icon: Users, requiresAuth: true, adminOnly: true },
      {
        href: "/admin/access-requests",
        label: "Access requests",
        icon: KeyRound,
        requiresAuth: true,
        adminOnly: true,
        badge: "accessRequests",
      },
    ],
  },
  {
    id: "admin-programs",
    label: "Programs",
    items: [
      {
        href: "/admin/challenges",
        label: "Campaigns",
        icon: Trophy,
        requiresAuth: true,
        workspaceAccessOnly: true,
        workspaceManagerOnly: true,
        activePrefixes: ["/workspace-admin"],
      },
      {
        href: "/workspaces",
        label: "Workspaces",
        icon: Building2,
        requiresAuth: true,
        workspaceAccessOnly: true,
      },
      { href: "/admin/themes", label: "Themes", icon: Palette, requiresAuth: true, adminOnly: true },
    ],
  },
  {
    id: "admin-content",
    label: "Content",
    items: [
      {
        href: "/admin/announcements",
        label: "Announcements",
        icon: Megaphone,
        requiresAuth: true,
        adminOnly: true,
      },
      {
        href: "/admin/memories",
        label: "Memories",
        icon: Images,
        requiresAuth: true,
        adminOnly: true,
      },
      {
        href: "/admin/feedback",
        label: "Feedback review",
        icon: MessageSquareHeart,
        requiresAuth: true,
        adminOnly: true,
      },
      {
        href: "/admin/whatsapp",
        label: "WhatsApp",
        icon: MessageCircle,
        requiresAuth: true,
        adminOnly: true,
      },
    ],
  },
  {
    id: "admin-operations",
    label: "Operations",
    items: [
      { href: "/admin/health", label: "Portal health", icon: Activity, requiresAuth: true, adminOnly: true },
      { href: "/admin/reports/operations", label: "Campaign operations", icon: BarChart3, requiresAuth: true, adminOnly: true },

      { href: "/admin/audit", label: "Audit log", icon: ScrollText, requiresAuth: true, adminOnly: true },
    ],
  },
];

/**
 * Routes that belong to the management area. `/workspaces` is included so a workspace manager
 * moving between their workspaces and their campaigns is not thrown back into the participant
 * rail part way through a task.
 */
const ADMIN_AREA_PREFIXES = ["/admin", "/workspace-admin", "/workspaces"];

/** True when the management area's navigation should replace the participant sections. */
export function isAdminArea(pathname: string): boolean {
  return ADMIN_AREA_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Flat list used by the public marketing header. */
export const PUBLIC_NAV: NavItem[] = [
  { href: "/", label: "Home", icon: Home, requiresAuth: false, exact: true },
];

/** Compact set for the mobile bottom bar. */
export const BOTTOM_NAV: NavItem[] = [
  { href: "/dashboard", label: "Home", icon: LayoutDashboard, requiresAuth: true },
  {
    href: "/challenges/:slug/tracker",
    label: "Log",
    icon: Activity,
    requiresAuth: true,
    challengeScoped: true,
  },
  { href: "/community", label: "Feed", icon: Newspaper, requiresAuth: true },
  { href: "/badges", label: "Badges", icon: Award, requiresAuth: true, requiresRegistration: true },
  { href: "/profile", label: "You", icon: User, requiresAuth: true },
];

export const TROPHY_ICON = Trophy;
export const SPARKLE_ICON = Sparkles;

/** Swaps the `:slug` placeholder, or returns null when there is no challenge. */
export function resolveHref(item: NavItem, slug: string | null): string | null {
  if (!item.challengeScoped) return item.href;
  if (!slug) return item.unscopedHref ?? null;
  return item.href.replace(":slug", slug);
}

/** True when `pathname` should light this item up. */
export function isActive(
  href: string,
  pathname: string,
  exact?: boolean,
  activePrefixes: string[] = [],
): boolean {
  if (activePrefixes.some(prefix => pathname.startsWith(prefix))) return true;
  if (exact) return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}
